import React, { useState } from "react";
import {
  TextField,
  Switch,
  FormControlLabel,
  Box,
  Stack,
  FormGroup,
  Checkbox,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "../../contexts/SnackbarProvider";
import { IScene, TSceneType, TStatus } from "./types";
import useCreateData from "../../hooks/useCreateData";
import useUpdateData from "../../hooks/useUpdateData";
import FormikSelect, { TOption } from "../../Components/FormikSelect";
import { IControl } from "../controls/types";
import DialogButtons from "../../Components/DialogButtons";
import { useTranslation } from "react-i18next";

const sceneTypeOptions: TOption<TSceneType>[] = [
  { value: "toggle_controls", label: "Toggle Controls" },
  { value: "enable_automations", label: "Enable Automations" },
];
const statusOptions: TOption<TStatus>[] = [{ value: "on" }, { value: "off" }];

interface ISceneFormProps {
  scene: IScene;
  controls: IControl[];
}

const SceneForm = (props: ISceneFormProps) => {
  const { scene, controls } = props;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const showMessage = useSnackbar();
  const { t } = useTranslation();

  const handleCancel = () => {
    showMessage({
      text: scene.name
        ? t("Editing scene SCENE cancelled", { scene })
        : t("Creating new scene cancelled"),
      severity: "info",
    });
    navigate("/scenes");
  };

  const createHandler = useCreateData();
  const updateHandler = useUpdateData();
  const formik = useFormik({
    initialValues: {
      name: scene.name,
      scene_type: scene.scene_type,
      is_enabled: scene.is_enabled,
      status: scene.status,
      checked: scene.control_ids.map((id) => id.toString()),
    },
    validationSchema: Yup.object({
      name: Yup.string().required(t("Required")),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      const allValues = {
        ...scene,
        ...values,
        status:
          values.scene_type === "enable_automations" ? "off" : values.status,
      };
      if (scene.id === -1) {
        createHandler(`scene "${allValues.name}"`, "/scenes", allValues);
      } else {
        updateHandler(
          `scene "${allValues.name}"`,
          `/scenes/${scene.id}`,
          allValues
        );
      }
      navigate("/scenes");
    },
  });
  return (
    <form onSubmit={formik.handleSubmit} data-cy="scene-form">
      <FormikProvider value={formik}>
        <Stack spacing={2} alignItems="flex-start">
          <TextField
            fullWidth
            autoFocus
            margin="normal"
            id="name"
            label={t("Name")}
            type="text"
            disabled={isSubmitting}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.name}
          ></TextField>
          <FormControlLabel
            control={
              <Switch
                id="is_enabled"
                name="is_enabled"
                checked={formik.values.is_enabled}
                onChange={formik.handleChange}
              />
            }
            label={t("Enabled")}
            labelPlacement="start"
          />
          <Box
            gap={1}
            display="flex"
            flexDirection="row"
            style={{ width: "100%" }}
          >
            <FormikSelect
              label={t("Scene Type")}
              name="scene_type"
              options={sceneTypeOptions}
              style={{ width: "70%" }}
              margin="none"
            />
            {formik.values.scene_type === "toggle_controls" && (
              <FormikSelect
                label={t("Status")}
                name="status"
                options={statusOptions}
                style={{ width: "30%" }}
                margin="none"
              />
            )}
          </Box>
          <FormGroup>
            {controls
              .sort(function (a: IControl, b: IControl) {
                return a.name.localeCompare(b.name);
              })
              .map((control) => (
                <FormControlLabel
                  key={control.id}
                  control={
                    <Checkbox
                      checked={formik.values.checked.includes(
                        control.id.toString()
                      )}
                    />
                  }
                  name={"checked"}
                  label={control.name}
                  value={control.id}
                  onChange={formik.handleChange}
                />
              ))}
          </FormGroup>
        </Stack>
        <DialogButtons
          isSubmitting={isSubmitting}
          submitButtonLabel="Save"
          resetCallback={handleCancel}
        />
      </FormikProvider>
    </form>
  );
};

export default SceneForm;
